import React, { useEffect, useMemo, useState } from "react";

import { ChevronDown, ChevronRight, Loader2, Plus } from "lucide-react";

import { useToast } from "../../../Components/Toast";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { roConfigErrorMessage, useRoConfig } from "../../../Hooks/useRoConfig";
import { RoMetricItem } from "../../../Types/RoConfig";

type Row = RoMetricItem & { source: "Master" | "Custom" };

const slugify = (name: string) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const SourceBadge: React.FC<{ source: Row["source"] }> = ({ source }) =>
  source === "Master" ? (
    <span className="inline-flex items-center rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs font-medium text-gray-600">
      Master
    </span>
  ) : (
    <span className="inline-flex items-center rounded-md border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700">
      Custom
    </span>
  );

const WeightCell: React.FC<{
  row: Row;
  disabled?: boolean;
  onChange: (pct: number) => void;
}> = ({ row, disabled, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(row.weightPct));

  if (!editing) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setValue(String(row.weightPct));
          setEditing(true);
        }}
        className="rounded-md px-2 py-1 text-sm font-medium text-gray-900 hover:bg-gray-100 disabled:opacity-50"
        title="Click to edit weight"
      >
        {row.weightPct}%
      </button>
    );
  }

  const commit = () => {
    const parsed = Math.max(0, Math.min(100, Number(value) || 0));
    onChange(parsed);
    setEditing(false);
  };

  return (
    <input
      autoFocus
      type="number"
      min={0}
      max={100}
      value={value}
      onChange={e => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") setEditing(false);
      }}
      className="w-16 rounded-md border border-violet-300 px-2 py-1 text-sm outline-none"
    />
  );
};

const AddMetricRow: React.FC<{
  onAdd: (name: string) => void;
  onCancel: () => void;
}> = ({ onAdd, onCancel }) => {
  const [name, setName] = useState("");
  return (
    <tr className="bg-violet-50/40">
      <td className="px-4 py-2.5">
        <input
          autoFocus
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="New metric name"
          className="w-full rounded-md border border-violet-300 px-2 py-1 text-sm outline-none focus:ring-2 focus:ring-violet-500/30"
          onKeyDown={e => {
            if (e.key === "Enter" && name.trim()) onAdd(name.trim());
            if (e.key === "Escape") onCancel();
          }}
        />
      </td>
      <td className="px-4 py-2.5" colSpan={3} />
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!name.trim()}
            onClick={() => name.trim() && onAdd(name.trim())}
            className="rounded-md bg-violet-600 px-2.5 py-1 text-xs font-medium text-white disabled:opacity-40"
          >
            Add
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
};

const DimensionAccordionItem: React.FC<{
  dimensionKey: string;
  dimensionLabel: string;
  rows: Row[];
  expanded: boolean;
  saving: boolean;
  onToggleExpand: () => void;
  onUpdateRows: (updater: (rows: Row[]) => Row[]) => void;
}> = ({
  dimensionKey,
  dimensionLabel,
  rows,
  expanded,
  saving,
  onToggleExpand,
  onUpdateRows,
}) => {
  const [adding, setAdding] = useState(false);
  const enabledCount = rows.filter(r => r.included).length;

  const toggleIncluded = (metricId: string) => {
    onUpdateRows(prev =>
      prev.map(r =>
        r.metricId === metricId ? { ...r, included: !r.included } : r
      )
    );
  };

  const setWeight = (metricId: string, pct: number) => {
    onUpdateRows(prev =>
      prev.map(r => (r.metricId === metricId ? { ...r, weightPct: pct } : r))
    );
  };

  const removeMetric = (metricId: string) => {
    onUpdateRows(prev => prev.filter(r => r.metricId !== metricId));
  };

  const addMetric = (name: string) => {
    onUpdateRows(prev => [
      ...prev,
      {
        dimensionKey,
        dimensionLabel,
        category: rows[0]?.category ?? "risk",
        metricId: `${slugify(name)}_${Date.now().toString(36)}`,
        metricName: name,
        included: true,
        sortOrder: prev.length + 1,
        weightPct: 0,
        source: "Custom",
      },
    ]);
    setAdding(false);
  };

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200">
      <button
        type="button"
        onClick={onToggleExpand}
        className="flex w-full items-center justify-between gap-2 bg-gray-50 px-4 py-2.5 text-left"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="font-mono text-xs text-gray-500">
            {dimensionKey}
          </span>
          — {dimensionLabel}
        </span>
        <span className="inline-flex items-center rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-600">
          {enabledCount}/{rows.length} enabled
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-2">
          <div className="mb-2 flex justify-end">
            <button
              type="button"
              disabled={saving}
              onClick={() => setAdding(true)}
              className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <Plus size={14} />
              Add metric
            </button>
          </div>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full border-collapse text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Metric
                  </th>
                  <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Source
                  </th>
                  <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Weight/State
                  </th>
                  <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Include
                  </th>
                  <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={row.metricId}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900">
                        {row.metricName}
                      </div>
                      <div className="font-mono text-[11px] text-gray-400">
                        {row.metricId}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <SourceBadge source={row.source} />
                    </td>
                    <td className="px-4 py-2.5">
                      <WeightCell
                        row={row}
                        disabled={saving}
                        onChange={pct => setWeight(row.metricId, pct)}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <ToggleSwitch
                        id={`ro-metric-${row.metricId}`}
                        checked={row.included}
                        disabled={saving}
                        onChange={() => toggleIncluded(row.metricId)}
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => removeMetric(row.metricId)}
                        className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                        title="Remove"
                      >
                        –
                      </button>
                    </td>
                  </tr>
                ))}
                {adding && (
                  <AddMetricRow
                    onAdd={addMetric}
                    onCancel={() => setAdding(false)}
                  />
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

function toRows(metrics: RoMetricItem[]): Row[] {
  return metrics.map(m => ({ ...m, source: "Master" as const }));
}

const RoMetricsPanel: React.FC = () => {
  const toast = useToast();
  const {
    riskDimensions,
    opportunityDimensions,
    dimensionWeights,
    metrics,
    loading,
    error,
    saveConfig,
  } = useRoConfig();

  const [rows, setRows] = useState<Row[]>([]);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState<"risk" | "opportunity">("risk");
  const [dimensionFilter, setDimensionFilter] = useState<string>("all");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!loading) {
      setRows(toRows(metrics));
      setHydrated(true);
    }
  }, [loading, metrics]);

  const dimensionOrder = useMemo(() => {
    const order = new Map<string, number>();
    [...riskDimensions, ...opportunityDimensions].forEach(d => {
      order.set(d.key, d.sortOrder);
    });
    return order;
  }, [riskDimensions, opportunityDimensions]);

  const dimensionsInScope = useMemo(() => {
    const seen = new Map<string, string>();
    rows
      .filter(r => r.category === scope)
      .forEach(r => seen.set(r.dimensionKey, r.dimensionLabel));
    return Array.from(seen.entries())
      .map(([key, label]) => ({ key, label }))
      .sort(
        (a, b) =>
          (dimensionOrder.get(a.key) ?? 999) -
          (dimensionOrder.get(b.key) ?? 999)
      );
  }, [rows, scope, dimensionOrder]);

  useEffect(() => {
    setDimensionFilter("all");
  }, [scope]);

  const groups = useMemo(() => {
    const filtered = rows.filter(
      r =>
        r.category === scope &&
        (dimensionFilter === "all" || r.dimensionKey === dimensionFilter)
    );
    const byDim = new Map<string, Row[]>();
    filtered.forEach(r => {
      const list = byDim.get(r.dimensionKey) ?? [];
      list.push(r);
      byDim.set(r.dimensionKey, list);
    });
    return dimensionsInScope
      .filter(d => byDim.has(d.key))
      .map(d => ({ ...d, rows: byDim.get(d.key) ?? [] }));
  }, [rows, scope, dimensionFilter, dimensionsInScope]);

  const persistMetrics = async (nextRows: Row[]) => {
    setSaving(true);
    try {
      await saveConfig({
        // Keep current dimension weights/include so a metrics-only edit does
        // not rely on an empty dimensions payload alone for UI consistency.
        dimensions: dimensionWeights.map(d => ({
          key: d.key,
          category: d.category,
          included: d.included,
          weight_pct: d.weightPct,
        })),
        metrics: nextRows.map(m => ({
          dimension_key: m.dimensionKey,
          category: m.category,
          metric_id: m.metricId,
          included: m.included,
          weight_pct: m.weightPct,
        })),
      });
      toast.success("R&O metrics saved.");
    } catch (e: unknown) {
      toast.error(roConfigErrorMessage(e, "Failed to save R&O metrics."));
      setRows(toRows(metrics));
    } finally {
      setSaving(false);
    }
  };

  const toggleExpand = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const updateDimensionRows = (
    dimensionKey: string,
    updater: (dimRows: Row[]) => Row[]
  ) => {
    setRows(prev => {
      const others = prev.filter(r => r.dimensionKey !== dimensionKey);
      const mine = prev.filter(r => r.dimensionKey === dimensionKey);
      const next = [...others, ...updater(mine)];
      void persistMetrics(next);
      return next;
    });
  };

  if (loading || !hydrated) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading R&amp;O metrics…
      </div>
    );
  }

  if (error) {
    return <p className="py-8 text-sm text-red-600">{error}</p>;
  }

  return (
    <div>
      {saving && (
        <div className="mb-3 flex items-center gap-2 text-xs text-gray-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Saving…
        </div>
      )}
      <div className="mb-4 flex flex-wrap gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">
            Scope
          </label>
          <select
            value={scope}
            onChange={e => setScope(e.target.value as "risk" | "opportunity")}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800"
          >
            <option value="risk">Risk</option>
            <option value="opportunity">Opportunity</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-500">
            Dimension
          </label>
          <select
            value={dimensionFilter}
            onChange={e => setDimensionFilter(e.target.value)}
            className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800"
          >
            <option value="all">All {scope} dimensions</option>
            {dimensionsInScope.map(d => (
              <option key={d.key} value={d.key}>
                {d.key} — {d.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {groups.map(group => (
          <DimensionAccordionItem
            key={group.key}
            dimensionKey={group.key}
            dimensionLabel={group.label}
            rows={group.rows}
            expanded={expandedKeys.has(group.key)}
            saving={saving}
            onToggleExpand={() => toggleExpand(group.key)}
            onUpdateRows={updater => updateDimensionRows(group.key, updater)}
          />
        ))}
      </div>
    </div>
  );
};

export default RoMetricsPanel;
